import { useMutation, useQuery } from 'react-query';

import { useHistory } from 'react-router-dom';

import { useForm } from 'react-hook-form';

import { useState } from '@hookstate/core';

import { yupResolver } from '@hookform/resolvers/yup';

import * as yup from 'yup';

import { createAccount } from '@services/queries/auth';
import { getManufacturers } from '@services/queries/manufacturers';
import { getStudios } from '@services/queries/studios';

import { useToast } from '@hooks';

import LogoExpandedImage from '@assets/svg/standard-logo.svg';

import { IStudio } from '@models/Studio.model';
import { IManufacturer } from '@models/Manufacturer.model';

import {
  Container,
  Body,
  Input,
  Label,
  Button,
  Select,
  Error,
  Account,
} from './SignUp.styles';

interface IFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: string;
  type: string;
  studioId?: string;
  manufacturerId?: string;
}

interface ISignUpBody {
  studios: IStudio[];
  manufacturers: IManufacturer[];
}

const SignUpBody = ({ studios, manufacturers }: ISignUpBody) => {
  const history = useHistory();

  const { toast } = useToast();

  const type = useState('studio');

  const mutationSignUp = useMutation(createAccount, {
    onSuccess: () => {
      toast.success('Account created successfully!');

      history.push('signin');
    },
  });

  const schema = yup.object().shape({
    name: yup.string().required(),
    email: yup.string().email().required(),
    password: yup.string().required(),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords must match')
      .required(),
    type: yup.string().required(),
    role: yup.string().required(),
    studioId: yup.string().uuid(),
    manufacturerId: yup.string().uuid(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      type: 'studio',
      role: 'admin',
    },
  });

  const onSubmit = (data: IFormData) => {
    if (data.type === 'studio') {
      delete data.manufacturerId;
    }
    if (data.type === 'manufacturer') {
      delete data.studioId;
    }

    mutationSignUp.mutate(data);
  };

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Body>
          <img src={LogoExpandedImage} alt="logo" />

          <div style={{ height: 24 }} />
          <Label>Type</Label>
          <div style={{ height: 8 }} />
          <Select
            ref={register('type').ref}
            name={register('type').name}
            onBlur={register('type').onBlur}
            onChange={e => {
              register('type').onChange(e);
              type.set(e.target.value);
            }}
          >
            <option value="studio">Studio</option>
            <option value="manufacturer">Manufacturer</option>
          </Select>
          {errors.type?.message && <Error>{errors.type?.message}</Error>}

          <div style={{ height: 16 }} />
          {type.get() === 'studio' ? (
            <>
              <Label>Studio</Label>
              <div style={{ height: 8 }} />
              <Select {...register('studioId')}>
                {studios.map(studio => (
                  <option key={studio.id} value={studio.id}>
                    {studio.name}
                  </option>
                ))}
              </Select>
              {errors.studioId?.message && (
                <Error>{errors.studioId?.message}</Error>
              )}
            </>
          ) : (
            <>
              <Label>Manufacturer</Label>
              <div style={{ height: 8 }} />
              <Select {...register('manufacturerId')}>
                {manufacturers.map(manufacturer => (
                  <option key={manufacturer.id} value={manufacturer.id}>
                    {manufacturer.name}
                  </option>
                ))}
              </Select>
              {errors.manufacturerId?.message && (
                <Error>{errors.manufacturerId?.message}</Error>
              )}
            </>
          )}

          <div style={{ height: 16 }} />
          <Label>Role</Label>
          <div style={{ height: 8 }} />
          <Select {...register('role')}>
            <option value="admin">Admin</option>
            <option value="client">Client</option>
          </Select>
          {errors.role?.message && <Error>{errors.role?.message}</Error>}

          <div style={{ height: 16 }} />
          <Label>Name</Label>
          <div style={{ height: 8 }} />
          <Input {...register('name')} />
          {errors.name?.message && <Error>{errors.name?.message}</Error>}

          <div style={{ height: 16 }} />
          <Label>E-mail</Label>
          <div style={{ height: 8 }} />
          <Input {...register('email')} />
          {errors.email?.message && <Error>{errors.email?.message}</Error>}

          <div style={{ height: 16 }} />
          <Label>Password</Label>
          <div style={{ height: 8 }} />
          <Input {...register('password')} type="password" />
          {errors.password?.message && (
            <Error>{errors.password?.message}</Error>
          )}

          <div style={{ height: 16 }} />
          <Label>Confirm Password</Label>
          <div style={{ height: 8 }} />
          <Input {...register('confirmPassword')} type="password" />
          {errors.confirmPassword?.message && (
            <Error>{errors.confirmPassword?.message}</Error>
          )}

          <div style={{ height: 16 }} />

          <Account onClick={() => history.push('/signin')}>
            Return to sign in
          </Account>

          <div style={{ height: 16 }} />
          <Button>Submit</Button>
        </Body>
      </form>
    </Container>
  );
};

const SignUp = () => {
  const manufacturersQuery = useQuery('manufacturers', getManufacturers);
  const studiosQuery = useQuery('studios', getStudios);

  if (manufacturersQuery.isLoading || studiosQuery.isLoading) {
    return <div>loading</div>;
  }

  if (
    manufacturersQuery.isError ||
    studiosQuery.isError ||
    !manufacturersQuery.data ||
    !studiosQuery.data
  ) {
    return <div>error</div>;
  }

  return (
    <SignUpBody
      studios={studiosQuery.data}
      manufacturers={manufacturersQuery.data}
    />
  );
};

export { SignUp };
